import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Search, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { DISCORD_URL, API } from "@/lib/config";

const STATUS_META = {
  paid: { label: "PAID — VERIFIED", cls: "text-[#ff4655] border-[#ff4655]/60 bg-[#180e12]", Icon: CheckCircle2 },
  awaiting_payment: { label: "PAYMENT PENDING — updates automatically", cls: "text-zinc-300 border-zinc-600 bg-zinc-900/60", Icon: Clock },
  refunded: { label: "REFUNDED", cls: "text-zinc-400 border-zinc-700 bg-zinc-900/60", Icon: XCircle },
};

const PaymentSuccess = () => {
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  const checkStatus = async (e) => {
    e.preventDefault();
    if (!ref.trim()) return;
    setLoading(true);
    setOrder(null);
    try {
      const res = await axios.get(`${API}/orders/${encodeURIComponent(ref.trim())}`);
      setOrder(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order not found — check your reference code.");
    } finally {
      setLoading(false);
    }
  };

  const meta = order ? STATUS_META[order.status] || STATUS_META.awaiting_payment : null;

  return (
    <div className="bg-[#0c0d12] text-[#f0f2f5] antialiased min-h-screen" data-testid="success-page">
      <Navbar />
      <main className="px-6 py-24">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 font-mono-x text-xs text-[#ff4655] mb-4">
            <span className="w-2 h-2 bg-[#ff4655] blink-dot"></span>
            CHECKOUT COMPLETE
          </div>
          <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-[0.9] text-white mb-6">
            Payment<br /><span className="text-[#ff4655]">Received.</span>
          </h1>
          <p className="text-zinc-300 text-base md:text-lg leading-relaxed mb-10 max-w-xl mx-auto">
            Your payment is linked to your Scythe order reference automatically. Final step:
            send your receipt and reference in Discord to unlock your model files.
          </p>

          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="success-discord-btn"
            className="inline-block px-10 py-4 bg-[#ff4655] hover:bg-white hover:text-black text-white font-extrabold uppercase text-xs tracking-widest transition-colors skew-x-[-8deg] mb-16"
          >
            <span className="inline-block skew-x-[8deg]">Verify In Discord</span>
          </a>

          <div className="p-6 bg-zinc-900/60 border border-zinc-800 text-left">
            <p className="font-mono-x text-[10px] uppercase tracking-widest text-[#ff4655] mb-3">
              Check Your Order Status
            </p>
            <form onSubmit={checkStatus} className="flex gap-2">
              <input
                type="text"
                value={ref}
                onChange={(e) => setRef(e.target.value.toUpperCase())}
                placeholder="SCY-XXXXXX"
                data-testid="track-reference-input"
                className="flex-1 bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm px-3 py-2.5 placeholder:text-zinc-600 focus:outline-none focus:border-[#ff4655] font-mono-x tracking-widest"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="track-submit-btn"
                className="px-5 py-2.5 bg-[#ff4655] hover:bg-white hover:text-black disabled:opacity-60 text-white font-extrabold text-xs uppercase tracking-widest transition-colors flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Check
              </button>
            </form>

            {order && meta && (
              <div data-testid="track-status-result" className={`mt-4 p-4 border flex items-center gap-3 ${meta.cls}`}>
                <meta.Icon className="w-5 h-5 shrink-0" />
                <div>
                  <div className="font-mono-x text-xs font-bold tracking-widest">{meta.label}</div>
                  <div className="text-zinc-400 text-[11px] mt-1 font-mono-x">
                    {order.reference} · {order.plan_name} · €{order.price_eur} · {order.game}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
